import { DateInputBlock } from '@coast/schemas';
import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { getTextPropsFromTextStyle } from '@shared/components/blocks/utils';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { Colors, grid } from '@shared/styles';
import { formatDateWithYear, formatTime } from '@shared/util/date';
import { PickerMode } from './types';

interface Props {
  block: DateInputBlock;
  pickerMode: PickerMode;
  onOpenPicker(mode: PickerMode): void;
  onClosePicker(): void;
}

export function MBCardInputBlockDateContent({
  block,
  pickerMode,
  onOpenPicker,
  onClosePicker,
}: Props) {
  const textProps = getTextPropsFromTextStyle(block.textStyle);
  const { values } = useCardFormContext();
  const value = values[block.name];

  const openDatePicker = useCallback(() => onOpenPicker(PickerMode.Date), [onOpenPicker]);
  const openTimePicker = useCallback(() => onOpenPicker(PickerMode.Time), [onOpenPicker]);

  if (!value) {
    return (
      <Text {...textProps} inactive>
        {block.placeholder}
      </Text>
    );
  }

  if (block.fieldType === 'time') {
    return <Text {...textProps}>{formatTime(value)}</Text>;
  }

  if (block.fieldType === 'date') {
    return <Text {...textProps}>{formatDateWithYear(value)}</Text>;
  }

  return (
    <View row centerVertically>
      <MBTouchableOpacity
        style={[styles.touchable]}
        onPress={pickerMode === PickerMode.Date ? onClosePicker : openDatePicker}
      >
        <Text {...textProps}>{formatDateWithYear(value)}</Text>
      </MBTouchableOpacity>
      <View flex />
      <MBTouchableOpacity
        style={[styles.touchable]}
        onPress={pickerMode === PickerMode.Time ? onClosePicker : openTimePicker}
      >
        <Text {...textProps}>{formatTime(value)}</Text>
      </MBTouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  touchable: {
    paddingVertical: grid(1),
    paddingHorizontal: grid(1.5),
    marginLeft: -grid(1.5),
  },
  highlighted: {
    backgroundColor: Colors.v2.gray05,
    borderRadius: grid(1),
  },
});
